import { z } from 'zod';

import {
  attributionModelSchema,
  attributionWindowSchema,
  compareModeSchema,
  metricKeySchema,
  type BreakdownResponse,
  type HierarchyResponse,
  type MetricKey,
  type RankingRow,
  type TimeseriesResponse,
} from './analytics.js';
import {
  daySchema,
  relativeRangeSchema,
  type DataFreshness,
  type DataState,
} from './common.js';
import { entityStatusSchema } from './enums.js';

/**
 * WIDGET TABANLI PANO — Wask'ın "Pano Oluştur" + "Akıllı Widgetlar" karşılığı.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * `SavedReport` ile farkı
 * ═══════════════════════════════════════════════════════════════════════════
 * Rapor bir SORU ("son 30 günde kampanyalarım nasıl gitti"), widget bir
 * YERLEŞİM parçası. Şema ikisini ayrı tablolarda tutuyor (`dashboard_widgets`
 * başlığındaki gerekçe) ve bu sözleşme de ayrı: raporun `metrics` + `columns`ı
 * tek bir tabloyu tarif ediyor, widget'ın `config`i ise widget TİPİNE göre
 * tamamen farklı alanlar taşıyor. Tek şemaya sığdırmak, her alanın "hangi
 * tipte anlamlı" sorusunu koda değil kullanıcının kafasına bırakırdı.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 `kind` iki yerde yazılı ama TEK kaynak
 * ═══════════════════════════════════════════════════════════════════════════
 * `DashboardWidget.kind` bir kolon (sorgulanabilir olsun diye) ve `config.kind`
 * ayrım alanı (`discriminatedUnion` onsuz çalışmaz). Sunucu kolonu HER ZAMAN
 * `config.kind`ten yazar — kullanıcı kolonu ayrıca gönderemez. Böylece ikisi
 * sapamaz; sapabilseydi "tipi timeseries, config'i breakdown" olan bir satır
 * doğar ve çözümleyici çalışma zamanında patlardı.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Aralık GÖRELİ saklanır
 * ═══════════════════════════════════════════════════════════════════════════
 * `SavedReportConfig` ile aynı karar: "son 7 gün" diye kurulan bir widget bir
 * ay sonra da son 7 günü göstermeli. Sabit tarih saklamak, panoyu kurulduğu
 * günde donduran sessiz bir hata olurdu — kullanıcı sayıların neden
 * değişmediğini haftalarca fark etmez.
 */

export const DASHBOARD_WIDGET_SCHEMA_VERSION = 1;

/**
 * Widget tipleri.
 *
 * Beşi de AYNI analytics servisini çağırır, kendi SQL'ini yazmaz — motorun
 * okuduğu sayı ile panonun gösterdiği sayı farklı olamaz (`analytics.ts`,
 * "ikinci bir SQL yazılmaz" kuralı).
 */
export const DASHBOARD_WIDGET_KINDS = [
  /// Tek büyük sayı + dönem karşılaştırması.
  'metric',
  /// Zaman serisi — en fazla 4 metrik, isteğe bağlı kırılım.
  'timeseries',
  /// Pay kırılımı (pasta/çubuk) — platform, hesap, kampanya ya da durum.
  'breakdown',
  /// En iyi / en kötü N varlık.
  'ranking',
  /// Metrik kolonları seçilebilen varlık tablosu (KM8).
  'table',
] as const;
export type DashboardWidgetKind = (typeof DASHBOARD_WIDGET_KINDS)[number];
export const dashboardWidgetKindSchema = z.enum(DASHBOARD_WIDGET_KINDS);

// ═══════════════════════════════════════════════════════════════════════════
// Izgara yerleşimi
// ═══════════════════════════════════════════════════════════════════════════

/**
 * 12 kolonluk ızgara — 2, 3, 4 ve 6'ya tam bölünüyor, yani yarım / üçte bir /
 * dörtte bir genişlik hepsi tam sayı. 10 kolonda "üçte bir" imkânsızdı.
 *
 * 🔴 Sunucu ÇAKIŞMA doğrulamıyor (`schema.prisma` da öyle diyor): iki widget'ın
 * üst üste gelmesi bir veri hatası değil, arayüz sorunu. Sunucunun burada
 * hakemlik etmesi, sürükle-bırak sırasındaki her ara durumu (ki geçici olarak
 * çakışır) reddetmek demekti.
 */
export const DASHBOARD_GRID_COLUMNS = 12;

/// Pano başına widget tavanı. Gerekçesi performans: `POST /dashboard/run`
/// her widget için ayrı bir toplama sorgusu çalıştırıyor ve 30 sorgu bir
/// sayfa yüklemesi için zaten üst sınır.
export const DASHBOARD_WIDGET_MAX = 30;

export const widgetLayoutSchema = z.object({
  x: z.coerce.number().int().min(0).max(DASHBOARD_GRID_COLUMNS - 1).default(0),
  /// Satır indeksi. Üst sınır yüksek çünkü ızgara dikeyde serbest büyür.
  y: z.coerce.number().int().min(0).max(999).default(0),
  w: z.coerce.number().int().min(1).max(DASHBOARD_GRID_COLUMNS).default(4),
  h: z.coerce.number().int().min(1).max(12).default(2),
});
export type WidgetLayout = z.infer<typeof widgetLayoutSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Ortak config tabanı
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Widget'ın kapsamı.
 *
 * `metricFilterSchema` ile aynı alanlar ama `z.coerce`/CSV yok: widget config'i
 * bir JSON GÖVDESİNDEN geliyor, sorgu dizesinden değil. CSV ayrıştırmasını
 * buraya taşımak "platforms=META,GOOGLE" gibi bir dizgiyi kabul edip diziye
 * çevirirdi — JSON'da o zaten bir dizi ve dizgi gelmesi bir hata.
 */
export const widgetFiltersSchema = z
  .object({
    platforms: z.array(z.enum(['META', 'GOOGLE', 'TIKTOK', 'LINKEDIN'])).max(4).optional(),
    adAccountIds: z.array(z.uuid()).max(50).optional(),
    campaignIds: z.array(z.uuid()).max(200).optional(),
    adSetIds: z.array(z.uuid()).max(200).optional(),
    adIds: z.array(z.uuid()).max(200).optional(),
    status: z.array(entityStatusSchema).max(10).optional(),
    minSpend: z.number().min(0).optional(),
    /// Kampanya/hesap adı araması. Widget'ta da var çünkü "adında 'marka'
    /// geçen kampanyaların ROAS'ı" meşru bir pano kutusu.
    q: z.string().trim().max(120).optional(),
  })
  .default({});
export type WidgetFilters = z.infer<typeof widgetFiltersSchema>;

/**
 * Her widget tipinin taşıdığı ortak alanlar.
 *
 * ⚠️ `attributionWindow`/`attributionModel` widget BAŞINA ezme. Verilmezse
 * workspace ayarı, o da yoksa `DEFAULT_ATTRIBUTION`. Panoda iki kutunun iki
 * farklı pencereyle sayması meşru bir istek ("son tıklama ile görüntülemeli
 * atfı yan yana koy") ve yanıt hangi pencereyle sayıldığını geri söylüyor.
 */
export const widgetBaseConfigSchema = z.object({
  schemaVersion: z.literal(DASHBOARD_WIDGET_SCHEMA_VERSION),
  dateRange: relativeRangeSchema.default('last_30d'),
  customFrom: daySchema.optional(),
  customTo: daySchema.optional(),
  compare: compareModeSchema.default('none'),
  compareFrom: daySchema.optional(),
  compareTo: daySchema.optional(),
  filters: widgetFiltersSchema,
  attributionWindow: attributionWindowSchema.optional(),
  attributionModel: attributionModelSchema.optional(),
});
export type WidgetBaseConfig = z.infer<typeof widgetBaseConfigSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Tip başına config
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Tek sayı kutusu.
 *
 * ⚠️ `metric: 'reach' | 'frequency'` seçilebilir ama çok günlü aralıkta değer
 * `null` döner (`aggregatableOver: 'singleDay'`, bkz. `analytics.ts` erişim
 * kapısı). Bu bir hata DEĞİL — çözümleyici `singleDayOnly: true` bayrağıyla
 * geri söylüyor ki arayüz boş kutuya "yalnız tek günlük aralıkta" yazabilsin,
 * kullanıcı bunu bozuk sanmasın.
 */
export const metricWidgetConfigSchema = widgetBaseConfigSchema.extend({
  kind: z.literal('metric'),
  metric: metricKeySchema,
  /// Küçük trend çizgisi. Ayrı bir zaman serisi sorgusu demek — varsayılanı
  /// `false`, çünkü on kutuluk bir panoda on ek sorgu eder.
  showSparkline: z.boolean().default(false),
});
export type MetricWidgetConfig = z.infer<typeof metricWidgetConfigSchema>;

export const timeseriesWidgetConfigSchema = widgetBaseConfigSchema.extend({
  kind: z.literal('timeseries'),
  /// En fazla 4 — `timeseriesQuerySchema` ile aynı tavan. Beşinci çizgi
  /// grafiği okunamaz yapıyor ve iki eksen zaten iki ölçek demek.
  metrics: z.array(metricKeySchema).min(1).max(4),
  groupBy: z.enum(['none', 'platform', 'account', 'campaign']).default('none'),
  granularity: z.enum(['day']).default('day'),
  /// Sunum tercihi. Sunucu için anlamsız ama config'te yaşıyor çünkü panoya
  /// ait; arayüzün ayrı bir tercih deposu tutması iki kaynak demekti.
  chartType: z.enum(['line', 'area', 'bar']).default('line'),
});
export type TimeseriesWidgetConfig = z.infer<typeof timeseriesWidgetConfigSchema>;

export const breakdownWidgetConfigSchema = widgetBaseConfigSchema.extend({
  kind: z.literal('breakdown'),
  metric: metricKeySchema.default('spend'),
  groupBy: z.enum(['platform', 'account', 'campaign', 'status']),
  limit: z.number().int().min(1).max(50).default(10),
  chartType: z.enum(['pie', 'donut', 'bar']).default('donut'),
});
export type BreakdownWidgetConfig = z.infer<typeof breakdownWidgetConfigSchema>;

export const rankingWidgetConfigSchema = widgetBaseConfigSchema.extend({
  kind: z.literal('ranking'),
  metric: metricKeySchema.default('roas'),
  dir: z.enum(['asc', 'desc']).default('desc'),
  limit: z.number().int().min(1).max(20).default(5),
  level: z.enum(['CAMPAIGN', 'ADSET', 'AD']).default('CAMPAIGN'),
});
export type RankingWidgetConfig = z.infer<typeof rankingWidgetConfigSchema>;

/**
 * Metrik kolonları seçilebilen tablo — Wask'ın "Metrik Ekle"si.
 *
 * `pageSize` tavanı 50: bu bir pano kutusu, rapor ekranı değil. 200 satırlık
 * bir tabloyu ızgara hücresine sığdırmak zaten mümkün değil ve pano
 * yüklemesini metrik sayısıyla çarpardı.
 */
export const tableWidgetConfigSchema = widgetBaseConfigSchema.extend({
  kind: z.literal('table'),
  columns: z.array(metricKeySchema).min(1).max(10),
  level: z.enum(['CAMPAIGN', 'ADSET', 'AD']).default('CAMPAIGN'),
  sort: z.string().max(40).default('spend'),
  dir: z.enum(['asc', 'desc']).default('desc'),
  pageSize: z.number().int().min(1).max(50).default(10),
});
export type TableWidgetConfig = z.infer<typeof tableWidgetConfigSchema>;

export const dashboardWidgetConfigSchema = z.discriminatedUnion('kind', [
  metricWidgetConfigSchema,
  timeseriesWidgetConfigSchema,
  breakdownWidgetConfigSchema,
  rankingWidgetConfigSchema,
  tableWidgetConfigSchema,
]);
export type DashboardWidgetConfig = z.infer<typeof dashboardWidgetConfigSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Çözümleyici çıktıları
// ═══════════════════════════════════════════════════════════════════════════

/// Her widget yanıtının taşıdığı ortak alanlar — hangi aralıkla, hangi
/// tazelikle. Kutunun altındaki "son güncelleme" notu bundan.
export interface WidgetResultMeta {
  state: DataState;
  range: { from: string; to: string };
  compareRange: { from: string; to: string } | null;
  currency: string | null;
  freshness: DataFreshness;
}

export interface MetricWidgetData extends WidgetResultMeta {
  kind: 'metric';
  metric: MetricKey;
  value: number | null;
  previous: number | null;
  deltaPct: number | null;
  /// 🔴 `value: null` iki şey demek olabilir: veri yok ya da metrik bu
  /// aralıkta toplanamıyor. Bu bayrak ikincisini ayırıyor — arayüz "—" ile
  /// "yalnız tek günlük aralıkta" arasında seçim yapabilsin.
  singleDayOnly: boolean;
  /// `showSparkline: false` ise `null`.
  sparkline: { date: string; value: number | null }[] | null;
}

export interface TimeseriesWidgetData extends WidgetResultMeta {
  kind: 'timeseries';
  series: TimeseriesResponse['series'];
  metrics: MetricKey[];
  filledDays: number;
}

export interface BreakdownWidgetData extends WidgetResultMeta {
  kind: 'breakdown';
  metric: MetricKey;
  slices: BreakdownResponse['slices'];
  otherValue: number;
  total: number;
}

export interface RankingWidgetData extends WidgetResultMeta {
  kind: 'ranking';
  metric: MetricKey;
  rows: RankingRow[];
}

export interface TableWidgetData extends WidgetResultMeta {
  kind: 'table';
  columns: MetricKey[];
  rows: HierarchyResponse['rows'];
  totals: HierarchyResponse['totals'];
  total: number;
  mixedCurrency: boolean;
}

export type DashboardWidgetData =
  | MetricWidgetData
  | TimeseriesWidgetData
  | BreakdownWidgetData
  | RankingWidgetData
  | TableWidgetData;

// ═══════════════════════════════════════════════════════════════════════════
// CRUD
// ═══════════════════════════════════════════════════════════════════════════

/**
 * `personal` = yalnız kuran kişinin panosu (`userId` dolu).
 * `workspace` = tüm üyelerin gördüğü ortak pano (`userId` null).
 *
 * 🔴 Yetki ayrımı: kişisel widget `report.read` (VIEWER) ile kurulur, ortak
 * widget `report.write` (MEMBER) ister. Gerekçe: kişisel pano kullanıcının
 * ZATEN görebildiği verinin kendi düzeni — onu kısıtlamak bir güvenlik
 * kazancı sağlamaz, sadece VIEWER'ı sabit bir ekrana mahkûm ederdi. Ortak
 * pano ise başkalarının ekranını değiştirmek demek ve o bir yazma işi.
 */
export const widgetScopeSchema = z.enum(['personal', 'workspace']);
export type WidgetScope = z.infer<typeof widgetScopeSchema>;

export const dashboardWidgetCreateSchema = z.object({
  title: z.string().trim().max(120).nullable().default(null),
  scope: widgetScopeSchema.default('personal'),
  config: dashboardWidgetConfigSchema,
  layout: widgetLayoutSchema.optional(),
});
export type DashboardWidgetCreateInput = z.infer<typeof dashboardWidgetCreateSchema>;

/**
 * ⚠️ `config` KISMİ DEĞİL: verilirse tamamı verilir.
 *
 * Kısmi birleştirme, ayrım alanı (`kind`) taşıyan bir birlik üzerinde
 * anlamsız — "kind'i breakdown yap ama metrics alanı kalsın" isteği geçerli
 * bir config üretmez. Arayüz zaten config'in tamamını elinde tutuyor.
 */
export const dashboardWidgetUpdateSchema = z
  .object({
    title: z.string().trim().max(120).nullable().optional(),
    config: dashboardWidgetConfigSchema.optional(),
    layout: widgetLayoutSchema.optional(),
  })
  .refine(
    (v) => v.title !== undefined || v.config !== undefined || v.layout !== undefined,
    'Değiştirilecek bir şey verin',
  );
export type DashboardWidgetUpdateInput = z.infer<typeof dashboardWidgetUpdateSchema>;

/**
 * Sürükle-bırak sonrası TOPLU yerleşim kaydı.
 *
 * Tek tek `PATCH` yerine tek çağrı: bir widget'ı taşımak komşularının da
 * `y`sini değiştiriyor ve her birini ayrı istekle yazmak, yarısı gitmiş bir
 * yerleşim bırakabilirdi (kullanıcı sekmeyi kapatırsa). Tek işlemde ya hepsi
 * ya hiçbiri.
 */
export const dashboardLayoutSaveSchema = z.object({
  widgets: z
    .array(widgetLayoutSchema.extend({ id: z.uuid() }))
    .min(1)
    .max(DASHBOARD_WIDGET_MAX),
});
export type DashboardLayoutSaveInput = z.infer<typeof dashboardLayoutSaveSchema>;

export const dashboardListQuerySchema = z.object({
  /// `all` = kişisel + ortak, kullanıcının gördüğü pano. Varsayılan bu:
  /// panoyu açan kullanıcı iki kümeyi birleştirilmiş görmek ister.
  scope: z.enum(['personal', 'workspace', 'all']).default('all'),
});
export type DashboardListQuery = z.infer<typeof dashboardListQuerySchema>;

export interface DashboardWidgetRow {
  id: string;
  kind: DashboardWidgetKind;
  title: string | null;
  scope: WidgetScope;
  config: DashboardWidgetConfig;
  x: number;
  y: number;
  w: number;
  h: number;
  createdAt: string;
  updatedAt: string;
  /// İstek sahibi bunu düzenleyebilir mi. Kişisel widget'ta sahibi, ortakta
  /// `report.write` taşıyan herkes.
  canEdit: boolean;
}

export interface DashboardListResponse {
  widgets: DashboardWidgetRow[];
  /// Hiç widget yoksa arayüz `DASHBOARD_WIDGET_PRESETS`ten kurulan varsayılan
  /// panoyu gösterir ve bu bayrak `true` döner. Boş ekran yerine dolu bir
  /// başlangıç: kullanıcı "pano nasıl kurulur"u örnekten öğreniyor.
  isDefault: boolean;
}

// ═══════════════════════════════════════════════════════════════════════════
// Çalıştırma
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Panonun tamamını tek çağrıda çalıştırır.
 *
 * 🔴 KISMİ BAŞARI BAŞARIDIR — Faz 3'teki `notify_external` kararıyla aynı
 * çizgi. Bir widget'ın sorgusu patlarsa (bozuk config, silinmiş hesap
 * kimliği) YALNIZ o kutu hata gösterir; kalan dokuzu veriyle dolar. Tümünü
 * 500'e düşürmek, tek bozuk kutu yüzünden panoyu kullanılamaz yapardı.
 */
export const dashboardRunSchema = z.object({
  /// Boşsa kullanıcının TÜM widget'ları. Dolu ise yalnız verilenler —
  /// arayüz bir kutuyu tek başına yenileyebilsin diye.
  widgetIds: z.array(z.uuid()).max(DASHBOARD_WIDGET_MAX).optional(),
  /// Panonun tamamına uygulanan aralık ezmesi (üstteki tarih seçici).
  /// Widget config'ine YAZILMAZ; yalnız bu çalıştırma için geçerli.
  dateRange: relativeRangeSchema.optional(),
  customFrom: daySchema.optional(),
  customTo: daySchema.optional(),
});
export type DashboardRunInput = z.infer<typeof dashboardRunSchema>;

export interface DashboardWidgetRunResult {
  widgetId: string;
  kind: DashboardWidgetKind;
  title: string | null;
  /// `error` doluysa `null`.
  data: DashboardWidgetData | null;
  error: { code: string; message: string } | null;
}

export interface DashboardRunResponse {
  results: DashboardWidgetRunResult[];
  /// Ezilmiş aralıkla mı çalıştı — arayüz "kaydedilmiş aralığa dön" der.
  rangeOverridden: boolean;
}

// ═══════════════════════════════════════════════════════════════════════════
// Akıllı widget'lar (hazır config kataloğu)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Wask'ın "Akıllı Widgetlar"ı — kullanıcı sıfırdan config yazmasın.
 *
 * Katalog KODDA, `workflow_templates` gibi tabloda değil. Fark: şablon
 * kullanıcının düzenlediği ve kurduğu bir VERİ (admin ekranı var), bu ise
 * arayüzün "ekle" menüsünün içeriği — düzenlenecek bir yanı yok ve bir
 * tablodan okumak, her pano açılışına bir sorgu daha eklerdi.
 *
 * `labelKey` çeviri anahtarı: etiket iki dilde ve sunucuda saklanan bir metin
 * dil eklendiğinde migration isterdi (`BlogTopic` ile aynı karar).
 */
export interface DashboardWidgetPreset {
  key: string;
  labelKey: string;
  kind: DashboardWidgetKind;
  /// Önerilen ızgara boyutu — kullanıcı sonra değiştirebilir.
  layout: Pick<WidgetLayout, 'w' | 'h'>;
  config: DashboardWidgetConfig;
}

const BASE = {
  schemaVersion: DASHBOARD_WIDGET_SCHEMA_VERSION,
  dateRange: 'last_30d',
  compare: 'previous',
  filters: {},
} as const;

export const DASHBOARD_WIDGET_PRESETS: readonly DashboardWidgetPreset[] = [
  {
    key: 'spend',
    labelKey: 'dashboard.widget.preset.spend',
    kind: 'metric',
    layout: { w: 3, h: 1 },
    config: { ...BASE, kind: 'metric', metric: 'spend', showSparkline: true },
  },
  {
    key: 'roas',
    labelKey: 'dashboard.widget.preset.roas',
    kind: 'metric',
    layout: { w: 3, h: 1 },
    config: { ...BASE, kind: 'metric', metric: 'roas', showSparkline: true },
  },
  {
    key: 'conversions',
    labelKey: 'dashboard.widget.preset.conversions',
    kind: 'metric',
    layout: { w: 3, h: 1 },
    config: { ...BASE, kind: 'metric', metric: 'conversions', showSparkline: false },
  },
  {
    key: 'cpa',
    labelKey: 'dashboard.widget.preset.cpa',
    kind: 'metric',
    layout: { w: 3, h: 1 },
    config: { ...BASE, kind: 'metric', metric: 'cpa', showSparkline: false },
  },
  {
    key: 'spend_revenue_trend',
    labelKey: 'dashboard.widget.preset.spendRevenueTrend',
    kind: 'timeseries',
    layout: { w: 8, h: 3 },
    config: {
      ...BASE,
      kind: 'timeseries',
      metrics: ['spend', 'revenue'],
      groupBy: 'none',
      granularity: 'day',
      chartType: 'area',
    },
  },
  {
    key: 'platform_split',
    labelKey: 'dashboard.widget.preset.platformSplit',
    kind: 'breakdown',
    layout: { w: 4, h: 3 },
    config: {
      ...BASE,
      kind: 'breakdown',
      metric: 'spend',
      groupBy: 'platform',
      limit: 10,
      chartType: 'donut',
    },
  },
  {
    key: 'top_campaigns',
    labelKey: 'dashboard.widget.preset.topCampaigns',
    kind: 'ranking',
    layout: { w: 6, h: 3 },
    config: {
      ...BASE,
      kind: 'ranking',
      metric: 'roas',
      dir: 'desc',
      limit: 5,
      level: 'CAMPAIGN',
    },
  },
  {
    key: 'worst_cpa',
    labelKey: 'dashboard.widget.preset.worstCpa',
    kind: 'ranking',
    layout: { w: 6, h: 3 },
    config: {
      ...BASE,
      kind: 'ranking',
      // 🔴 `dir: 'desc'` DOĞRU: CPA'da yüksek olan KÖTÜ. "En kötü"yü `asc` ile
      // istemek metriğin `betterWhen`ini burada ikinci kez yorumlamak olurdu;
      // sıralama yönü ham, yorum arayüzün başlığında.
      metric: 'cpa',
      dir: 'desc',
      limit: 5,
      level: 'CAMPAIGN',
    },
  },
  {
    key: 'campaign_table',
    labelKey: 'dashboard.widget.preset.campaignTable',
    kind: 'table',
    layout: { w: 12, h: 4 },
    config: {
      ...BASE,
      kind: 'table',
      columns: ['spend', 'impressions', 'clicks', 'ctr', 'conversions', 'roas'],
      level: 'CAMPAIGN',
      sort: 'spend',
      dir: 'desc',
      pageSize: 10,
    },
  },
  {
    key: 'creative_fatigue',
    labelKey: 'dashboard.widget.preset.creativeFatigue',
    kind: 'timeseries',
    layout: { w: 6, h: 3 },
    config: {
      ...BASE,
      kind: 'timeseries',
      // Frekans TOPLAMDA `null` ama zaman serisinde gruplama güne göre olduğu
      // için çalışıyor (`analytics.ts` erişim kapısı). Kreatif yorgunluğunu
      // panoda gösterebilen tek biçim bu.
      metrics: ['frequency', 'ctr'],
      groupBy: 'none',
      granularity: 'day',
      chartType: 'line',
    },
  },
];

/// Hiç widget'ı olmayan kullanıcıya gösterilen başlangıç panosu.
export const DEFAULT_DASHBOARD_PRESET_KEYS: readonly string[] = [
  'spend',
  'roas',
  'conversions',
  'cpa',
  'spend_revenue_trend',
  'platform_split',
  'campaign_table',
];

export const DashboardErrorCode = {
  WIDGET_NOT_FOUND: 'WIDGET_NOT_FOUND',
  /// Pano tavanı doldu (`DASHBOARD_WIDGET_MAX`).
  WIDGET_LIMIT_REACHED: 'WIDGET_LIMIT_REACHED',
  /// Ortak panoya kişisel yetkiyle dokunma girişimi.
  WIDGET_SCOPE_FORBIDDEN: 'WIDGET_SCOPE_FORBIDDEN',
  /// Yerleşim kaydında istek sahibinin görmediği bir kimlik geçti.
  WIDGET_LAYOUT_MISMATCH: 'WIDGET_LAYOUT_MISMATCH',
} as const;
export type DashboardErrorCode = (typeof DashboardErrorCode)[keyof typeof DashboardErrorCode];
